import {GlobalEventBus} from "../GlobalEventBus";
import {
    TRIGGER_HIDE_REASONS,
    TRIGGER_VISIBILITY_EVENTS,
    TriggerHideReason
} from "../../Events/Widget/TriggerVisibility";
import {CHECKOUT_EVENTS} from "../../Events/Checkout/events";
import {WIDGET_STATE_EVENTS} from "../../Events/Widget/State";
import ROUTE_NAMES from "../../Widgets/ModalWidget/Routes/constants";

export class TriggerVisibilityService {
    private globalReasons = new Set<TriggerHideReason>();
    private widgetReasons = new Map<string, Set<TriggerHideReason>>();
    private widgetsShowingCheckout = new Set<string>();
    private eventBus: typeof GlobalEventBus;

    constructor(eventBus: typeof GlobalEventBus) {
        this.eventBus = eventBus;
        this.listenToCheckoutEvents();
        this.listenToNavigationEvents();
    }

    /**
     * Hides the trigger for the given reason.
     * @param reason why the trigger has to go away
     * @param widgetId limits the request to one widget; omit it for all widgets
     */
    hide = (reason: TriggerHideReason, widgetId?: string): void => {
        this.change(reason, widgetId, (reasons) => reasons.add(reason));
    }

    /**
     * Withdraws a previous hide request. The trigger reappears only if nothing
     * else is still hiding it.
     * @param reason the reason that no longer applies
     * @param widgetId the widget the request was scoped to, if any
     */
    show = (reason: TriggerHideReason, widgetId?: string): void => {
        this.change(reason, widgetId, (reasons) => reasons.delete(reason));
    }

    /**
     * @param widgetId the widget asking about itself
     * @returns whether that widget may show its trigger
     */
    isVisibleFor = (widgetId?: string): boolean => {
        if (this.globalReasons.size > 0) { return false; }
        if (!widgetId) { return true; }

        return !this.widgetReasons.get(widgetId)?.size;
    }

    /**
     * Drops everything remembered about a widget that went away, so a widget
     * id that comes back does not inherit a stale hide request.
     * @param widgetId the widget that unmounted
     */
    forget = (widgetId: string): void => {
        this.widgetReasons.delete(widgetId);
    }

    private reasonsFor = (widgetId?: string): Set<TriggerHideReason> => {
        if (!widgetId) { return this.globalReasons; }

        const existing = this.widgetReasons.get(widgetId);
        if (existing) { return existing; }

        const created = new Set<TriggerHideReason>();
        this.widgetReasons.set(widgetId, created);

        return created;
    }

    private change = (
        reason: TriggerHideReason,
        widgetId: string | undefined,
        mutate: (reasons: Set<TriggerHideReason>) => void
    ): void => {
        const reasons = this.reasonsFor(widgetId);
        const wasVisible = this.isVisibleFor(widgetId);

        mutate(reasons);

        const visible = this.isVisibleFor(widgetId);
        if (visible === wasVisible) { return; }

        this.eventBus.emit(TRIGGER_VISIBILITY_EVENTS.TRIGGER_VISIBILITY_CHANGED, {
            widgetId,
            reason,
            visible
        });
    }

    /**
     * A checkout we opened ourselves reports no closing of its own: it is the
     * widget navigation that decides when the trigger comes back, so those
     * openings are left to the router.
     */
    private listenToNavigationEvents = () => {
        this.eventBus.listen(WIDGET_STATE_EVENTS.WIDGET_NAVIGATION_CHANGED, ({name, widget}) => {
            if (name === ROUTE_NAMES.CHECKOUT) {
                this.widgetsShowingCheckout.add(widget.id);
                return;
            }

            this.widgetsShowingCheckout.delete(widget.id);
            if (this.widgetsShowingCheckout.size === 0) {
                // Nothing of ours is on a checkout any more, so a hide left
                // behind by one has to go.
                this.show(TRIGGER_HIDE_REASONS.EXTERNAL_CHECKOUT);
            }
        });
    }

    /**
     * The checkout opened outside this page: no widget should be competing
     * with it for the corner until it closes.
     */
    private listenToCheckoutEvents = () => {
        this.eventBus.listen(CHECKOUT_EVENTS.CHECKOUT_OPENED, () => {
            if (this.widgetsShowingCheckout.size > 0) { return; }

            this.hide(TRIGGER_HIDE_REASONS.EXTERNAL_CHECKOUT);
        });

        this.eventBus.listen(CHECKOUT_EVENTS.CHECKOUT_CLOSED, () => {
            this.show(TRIGGER_HIDE_REASONS.EXTERNAL_CHECKOUT);
        });
    }
}

/**
 * The page-wide instance. Widgets and event adapters share this one so that
 * every hide request is weighed against all the others.
 */
export const TriggerVisibility = new TriggerVisibilityService(GlobalEventBus);
